/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { HttpResponse } from './http-response';
import Axios, {
	AxiosInstance,
	AxiosRequestConfig,
	AxiosRequestHeaders,
	AxiosResponse,
	RawAxiosRequestHeaders,
} from 'axios';

export class Http {
	/**
	 * The request configuration.
	 */
	private request: AxiosRequestConfig;

	/**
	 * The request configuration.
	 */
	private readonly axiosInstance: AxiosInstance;

	/**
	 * The payload format for a JSON or form-url-encoded request.
	 */
	private bodyFormat: BodyFormat = 'json';

	/**
	 * Create a new pending HTTP request instance.
	 */
	constructor() {
		this.request = {};
		this.axiosInstance = Axios.create();

		this.asJson();
	}

	/**
	 * Create a reusable Http instance.
	 *
	 * @returns {Http}
	 */
	static create(): Http {
		return new this();
	}

	/**
	 * Returns the Axios request config.
	 *
	 * @returns {AxiosRequestConfig}
	 */
	requestConfig(): AxiosRequestConfig {
		return this.request;
	}

	/**
	 * Returns the Axios instance.
	 *
	 * @returns {AxiosInstance}
	 */
	axios(): AxiosInstance {
		return this.axiosInstance;
	}

	/**
	 * Use the given `baseUrl` for all requests.
	 *
	 * @param {String} baseUrl
	 *
	 * @returns {Http}
	 */
	static withBaseUrl(baseUrl: string): Http {
		return new this().withBaseUrl(baseUrl);
	}

	/**
	 * Use the given `baseUrl` for all requests.
	 *
	 * @param {String} baseUrl
	 *
	 * @returns {Http}
	 */
	withBaseUrl(baseUrl: string): this {
		if (typeof baseUrl !== 'string') {
			throw new Error(`The base URL must be a string. Received "${typeof baseUrl}"`);
		}

		this.request.baseURL = baseUrl;

		return this;
	}

	/**
	 * Add request headers.
	 *
	 * @param {AxiosRequestHeaders} headers
	 *
	 * @returns {Http}
	 */
	static withHeaders(headers: Partial<RawAxiosRequestHeaders>): Http {
		return new this().withHeaders(headers);
	}

	/**
	 * Add request headers.
	 *
	 * @param {AxiosRequestHeaders} headers
	 *
	 * @returns {Http}
	 */
	withHeaders(headers: Partial<RawAxiosRequestHeaders>): this {
		this.request.headers = { ...this.request.headers, ...headers };

		return this;
	}

	/**
	 * Add query parameters to the request.
	 *
	 * @param {Object} queryParams
	 *
	 * @returns {Http}
	 */
	static withQueryParams(queryParams: object): Http {
		return new this().withQueryParams(queryParams);
	}

	/**
	 * Add query parameters to the request.
	 *
	 * @param {Object} queryParams
	 *
	 * @returns {Http}
	 */
	withQueryParams(queryParams: object): this {
		this.request.params = { ...this.request.params, ...queryParams };

		return this;
	}

	/**
	 * Add basic authentication via `username` and `password` to the request.
	 *
	 * @param {String} username
	 * @param {String} password
	 *
	 * @returns {Http}
	 */
	static withBasicAuth(username: string, password: string): Http {
		return new this().withBasicAuth(username, password);
	}

	/**
	 * Add basic authentication via `username` and `password` to the request.
	 *
	 * @param {String} username
	 * @param {String} password
	 *
	 * @returns {Http}
	 */
	withBasicAuth(username: string, password: string): this {
		this.request.auth = { username, password };

		return this;
	}

	/**
	 * Add an authorization `token` to the request.
	 *
	 * @param {String} token
	 * @param {String} type
	 *
	 * @returns {Http}
	 */
	static withToken(token: string, type?: string): Http {
		return new this().withToken(token, type);
	}

	/**
	 * Add an authorization `token` to the request.
	 *
	 * @param {String} token
	 * @param {String} type
	 *
	 * @returns {Http}
	 */
	withToken(token: string, type = 'Bearer'): this {
		return this.withHeaders({
			Authorization: `${type} ${token}`.trim(),
		});
	}

	/**
	 * Merge your own custom Axios options into the request.
	 *
	 * @param {Object} options
	 *
	 * @returns {Http}
	 */
	static withOptions<D = any>(options: AxiosRequestConfig<D>): Http {
		return new this().withOptions(options);
	}

	/**
	 * Merge your own custom Axios options into the request.
	 *
	 * @param {Object} options
	 *
	 * @returns {Http}
	 */
	withOptions<D = any>(options: AxiosRequestConfig<D> = {}): this {
		Object.assign(this.request, options);

		return this;
	}

	/**
	 * Add request payload.
	 *
	 * @param {Object} payload
	 *
	 * @returns {Http}
	 */
	static withPayload(payload: any): Http {
		return new this().withPayload(payload);
	}

	/**
	 * Add a request payload.
	 *
	 * @param {*} data
	 *
	 * @returns {Http}
	 */
	withPayload(data: any): this {
		this.request.data = data;

		return this;
	}

	/**
	 * Define the request timeout in milliseconds.
	 *
	 * @param {Number} timeout
	 *
	 * @returns {Http}
	 */
	static withTimeout(timeout: number): Http {
		return new this().withTimeout(timeout);
	}

	/**
	 * Define the request `timeout` in milliseconds.
	 *
	 * @param {Number} timeout
	 *
	 * @returns {Http}
	 */
	withTimeout(timeout: number): this {
		this.request.timeout = timeout;

		return this;
	}

	/**
	 * Define the request timeout in seconds.
	 *
	 * @param {Number} timeout
	 *
	 * @returns {Http}
	 */
	static withTimeoutInSeconds(timeout: number): Http {
		return new this().withTimeoutInSeconds(timeout);
	}

	/**
	 * Define the request `timeout` in seconds.
	 *
	 * @param {Number} timeout
	 *
	 * @returns {Http}
	 */
	withTimeoutInSeconds(timeout: number): this {
		return this.withTimeout(timeout * 1000);
	}

	/**
	 * Tell Http to send the request as JSON payload.
	 *
	 * @returns {Http}
	 */
	static asJson(): Http {
		return new this().asJson();
	}

	/**
	 * Tell Http to send the request as JSON payload.
	 *
	 * @returns {Http}
	 */
	asJson(): this {
		return this.payloadFormat('json').contentType('application/json');
	}

	/**
	 * Tell Http to send the request as form parameters,
	 * encoded as URL query parameters.
	 *
	 * @returns {Http}
	 */
	static asFormParams(): Http {
		return new this().asFormParams();
	}

	/**
	 * Tell Http to send the request as form parameters,
	 * encoded as URL query parameters.
	 *
	 * @returns {Http}
	 */
	asFormParams(): this {
		return this.payloadFormat('formParams').contentType('application/x-www-form-urlencoded');
	}

	/**
	 * Set the request payload format.
	 *
	 * @param {String} format
	 *
	 * @returns {Http}
	 */
	payloadFormat(format: BodyFormat): this {
		this.bodyFormat = format;

		return this;
	}

	/**
	 * Set the `Accept` request header. This indicates what
	 * content type the server should return.
	 *
	 * @param {String} accept
	 *
	 * @returns {Http}
	 */
	static accept(accept: string): Http {
		return new this().accept(accept);
	}

	/**
	 * Set the `Accept` request header. This indicates what
	 * content type the server should return.
	 *
	 * @param {String} accept
	 *
	 * @returns {Http}
	 */
	accept(accept: string): this {
		return this.withHeaders({ Accept: accept });
	}

	/**
	 * Set the `Accept` request header to JSON. This indicates
	 * that the server should return JSON data.
	 *
	 * @param {String} accept
	 *
	 * @returns {Http}
	 */
	static acceptJson(): Http {
		return new this().acceptJson();
	}

	/**
	 * Set the `Accept` request header to JSON. This indicates
	 * that the server should return JSON data.
	 *
	 * @param {String} accept
	 *
	 * @returns {Http}
	 */
	acceptJson(): this {
		return this.accept('application/json');
	}

	/**
	 * Set the `Content-Type` request header.
	 *
	 * @param {String} accept
	 *
	 * @returns {Http}
	 */
	static contentType(contentType: string): Http {
		return new this().contentType(contentType);
	}

	/**
	 * Set the `Content-Type` request header.
	 *
	 * @param {String} contentType
	 *
	 * @returns {Http}
	 */
	contentType(contentType: string): this {
		return this.withHeaders({ 'Content-Type': contentType });
	}

	/**
	 * Send an HTTP GET request, optionally with the given `queryParams`.
	 *
	 * @param {String} url
	 * @param {Object} queryParams
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	static async get<R>(url: string, queryParams: object): Promise<HttpResponse<R>> {
		return new this().get(url, queryParams);
	}

	/**
	 * Send an HTTP GET request, optionally with the given `queryParams`.
	 *
	 * @param {String} url
	 * @param {Object} queryParams
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	async get<R>(url: string, queryParams: object = {}): Promise<HttpResponse<R>> {
		this.withQueryParams(queryParams);

		return this.send<R>('GET', url);
	}

	/**
	 * Send an HTTP POST request, optionally with the given `payload`.
	 *
	 * @param {String} url
	 * @param {Object} payload
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	static async post<R>(url: string, payload?: any): Promise<HttpResponse<R>> {
		return new this().post(url, payload);
	}

	/**
	 * Send an HTTP POST request, optionally with the given `payload`.
	 *
	 * @param {String} url
	 * @param {Object} payload
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	async post<R>(url: string, payload?: any): Promise<HttpResponse<R>> {
		if (payload) {
			this.withPayload(payload);
		}

		return this.send<R>('POST', url);
	}

	/**
	 * Send an HTTP PUT request, optionally with the given `payload`.
	 *
	 * @param {String} url
	 * @param {Object} payload
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	static async put<R>(url: string, payload?: any): Promise<HttpResponse<R>> {
		return new this().put(url, payload);
	}

	/**
	 * Send an HTTP PUT request, optionally with the given `payload`.
	 *
	 * @param {String} url
	 * @param {Object} payload
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	async put<R>(url: string, payload?: any): Promise<HttpResponse<R>> {
		if (payload) {
			this.withPayload(payload);
		}

		return this.send<R>('PUT', url);
	}

	/**
	 * Send an HTTP PATCH request, optionally with the given `payload`.
	 *
	 * @param {String} url
	 * @param {Object} payload
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	static async patch<R>(url: string, payload?: any): Promise<HttpResponse<R>> {
		return new this().patch(url, payload);
	}

	/**
	 * Send an HTTP PATCH request, optionally with the given `payload`.
	 *
	 * @param {String} url
	 * @param {Object} payload
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	async patch<R>(url: string, payload?: any): Promise<HttpResponse<R>> {
		if (payload) {
			this.withPayload(payload);
		}

		return this.send<R>('PATCH', url);
	}

	/**
	 * Send an HTTP DELETE request, optionally with the given `queryParams`.
	 *
	 * @param {String} url
	 * @param {Object} queryParams
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	static async delete<R>(url: string, queryParams: object): Promise<HttpResponse<R>> {
		return new this().delete(url, queryParams);
	}

	/**
	 * Send an HTTP DELETE request, optionally with the given `queryParams`.
	 *
	 * @param {String} url
	 * @param {Object} queryParams
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	async delete<R>(url: string, queryParams: object = {}): Promise<HttpResponse<R>> {
		this.withQueryParams(queryParams);

		return this.send<R>('DELETE', url);
	}

	/**
	 * Send an HTTP OPTIONS request, optionally with the given `queryParams`.
	 *
	 * @param {String} url
	 * @param {Object} queryParams
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	static async options<R>(url: string, queryParams: object): Promise<HttpResponse<R>> {
		return new this().options(url, queryParams);
	}

	/**
	 * Send an HTTP OPTIONS request, optionally with the given `queryParams`.
	 *
	 * @param {String} url
	 * @param {Object} queryParams
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	async options<R>(url: string, queryParams: object = {}): Promise<HttpResponse<R>> {
		this.withQueryParams(queryParams);

		return this.send<R>('OPTIONS', url);
	}

	/**
	 * Send the HTTP request.
	 *
	 * @param {String} method
	 * @param {String} url
	 *
	 * @returns {HttpResponse}
	 *
	 * @throws
	 */
	async send<R>(method: HttpMethod, url: string): Promise<HttpResponse<R>> {
		try {
			return new HttpResponse<R>(await this.createAndSendRequest(method, url));
		} catch (error: any) {
			if (error.response) {
				return new HttpResponse(error.response);
			}

			throw error;
		}
	}

	/**
	 * Create and send the HTTP request.
	 *
	 * @param {String} method
	 * @param {String} url
	 *
	 * @returns {Request}
	 */
	async createAndSendRequest(method: HttpMethod, url: string): Promise<AxiosResponse> {
		return await this.axiosInstance({
			url,
			method,
			withCredentials: false,
			...this.request,
			data: this.prepareRequestPayload(),
		});
	}

	/**
	 * Returns the request payload depending on the selected request payload format.
	 */
	prepareRequestPayload(): any {
		return this.bodyFormat === 'formParams' ? new URLSearchParams(this.request.data).toString() : this.request.data;
	}
}

type BodyFormat = 'json' | 'formParams';

type HttpMethod = 'GET' | 'HEAD' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS';
